/**
 * Dashboard v2 mid-grid — Fork G.
 *
 * Fetches `/api/dashboard/:eventId/extras` and renders four drill-down cards
 * in a responsive grid beneath the hero KPI tiles:
 *   1. Plan Mix (donut)
 *   2. Kiosk Throughput (horizontal bars)
 *   3. Top Attractions (ranked list)
 *   4. Customer Geography (placeholder — customer_identity_graph is staged)
 *
 * Graceful empty states per brief hard-stop: "prefer graceful empty-state
 * rendering over backend changes" for missing data.
 *
 * Styling uses the locked Butler palette (#0070AB, #ED00BE, #FFF662,
 * #00C7CA) and MM admin-portal neutrals — no Mixpanel purple per Brian's
 * white-label directive.
 */

import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { BarChart3, Gauge, MapPin, Ticket } from 'lucide-react'

interface PlanMixRow {
  rank: number
  plan_name: string
  orders: number
  revenue: number
  pct_of_orders: number
}

interface KioskThroughputRow {
  rank: number
  kiosk_group_name: string
  placement_type: string | null
  orders: number
  revenue: number
  new_band_orders: number
  reload_orders: number
}

interface TopAttractionRow {
  rank: number
  attraction_id: number
  attraction_name: string
  attraction_type: string
  scans: number
  unique_guests: number
  revenue: number
  repeat_play_pct: number
}

interface GeographyPayload {
  status: string
  reason?: string
}

interface DashboardExtras {
  source: 'pg'
  event_id: number
  plan_mix: PlanMixRow[]
  kiosk_throughput: KioskThroughputRow[]
  top_attractions: TopAttractionRow[]
  geography: GeographyPayload
  generated_at: string
}

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n)

// Butler-locked palette — donut slices rotate through these in order.
const SLICE_COLORS = [
  '#0070AB',
  '#00C7CA',
  '#ED00BE',
  '#FFD400',
  '#005B92',
  '#008E91',
  '#9B0080',
  '#8697A8',
]

function CardShell({
  title,
  icon,
  children,
  hint,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  hint?: string
}) {
  return (
    <section className="flex min-h-[20rem] flex-col overflow-hidden rounded-card border border-hairline bg-white shadow-card-soft dark:border-slate-700 dark:bg-slate-800">
      <header className="flex items-center gap-2 border-b border-hairline px-4 py-2.5 dark:border-slate-700">
        <span className="text-butler-600 dark:text-butler-300" aria-hidden="true">
          {icon}
        </span>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-butler-700 dark:text-butler-200">
          {title}
        </h3>
        {hint ? (
          <span className="ml-auto text-[10px] font-medium text-ink-soft dark:text-slate-400">
            {hint}
          </span>
        ) : null}
      </header>
      <div className="flex flex-1 flex-col p-3">{children}</div>
    </section>
  )
}

function EmptyCardBody({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center text-xs text-ink-soft dark:text-slate-400">
      {label}
    </div>
  )
}

function PlanMixCard({ rows }: { rows: PlanMixRow[] }) {
  if (rows.length === 0) {
    return (
      <CardShell title="Plan Mix" icon={<Ticket className="h-4 w-4" />}>
        <EmptyCardBody label="No plan sales yet for this event." />
      </CardShell>
    )
  }
  // Detect enrichment-pending state: when >50% of orders land in the
  // "Unclassified" bucket, the `canonical_plan_id` backfill hasn't run yet
  // for this event. Codex pass 2 (2026-04-18) flagged this as silent data
  // risk — surface a hint so operators know the labels are provisional.
  const unclassifiedPct =
    rows.find((r) => r.plan_name === 'Unclassified')?.pct_of_orders ?? 0
  const enrichmentPending = unclassifiedPct > 50
  const chartData = rows.map((r, idx) => ({
    name: r.plan_name,
    value: r.orders,
    fill: SLICE_COLORS[idx % SLICE_COLORS.length],
  }))
  return (
    <CardShell
      title="Plan Mix"
      icon={<Ticket className="h-4 w-4" />}
      hint={
        enrichmentPending
          ? 'Plan mapping pending'
          : `${rows.length} plan${rows.length === 1 ? '' : 's'}`
      }
    >
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              stroke="#fff"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {chartData.map((entry, idx) => (
                <Cell key={`slice-${idx}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [fmtNum(value), name]}
              contentStyle={{
                borderRadius: 4,
                border: '1px solid #E9EBF2',
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {rows.slice(0, 4).map((r, idx) => (
          <li
            key={r.plan_name}
            className="flex items-center justify-between gap-2 text-ink dark:text-slate-200"
          >
            <span className="flex items-center gap-2 truncate">
              <span
                className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                style={{ background: SLICE_COLORS[idx % SLICE_COLORS.length] }}
              />
              <span className="truncate">{r.plan_name}</span>
            </span>
            <span className="flex-shrink-0 font-mono text-ink-soft dark:text-slate-400">
              {fmtNum(r.orders)} · {r.pct_of_orders}%
            </span>
          </li>
        ))}
      </ul>
    </CardShell>
  )
}

function KioskThroughputCard({ rows }: { rows: KioskThroughputRow[] }) {
  if (rows.length === 0) {
    return (
      <CardShell title="Kiosk Throughput" icon={<Gauge className="h-4 w-4" />}>
        <EmptyCardBody label="No kiosk activity yet." />
      </CardShell>
    )
  }
  const chartData = rows.slice(0, 6).map((r) => ({
    name: r.kiosk_group_name,
    orders: r.orders,
    new: r.new_band_orders,
    reload: r.reload_orders,
  }))
  // Codex pass 2 (2026-04-18): ETL currently writes `new_band_order_count`
  // and `reload_order_count` as placeholder 0s until enrichment runs.
  // Detect that state (all rows have 0 new + 0 reload despite nonzero
  // total orders) and hint instead of rendering a convincing-looking but
  // fake stacked-bar split.
  const splitsEnrichmentPending =
    rows.length > 0 &&
    rows.some((r) => r.orders > 0) &&
    rows.every((r) => r.new_band_orders === 0 && r.reload_orders === 0)
  return (
    <CardShell
      title="Kiosk Throughput"
      icon={<Gauge className="h-4 w-4" />}
      hint={
        splitsEnrichmentPending
          ? 'New/reload split pending'
          : `top ${Math.min(rows.length, 6)}`
      }
    >
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 4, right: 10, left: 8, bottom: 4 }}
          >
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: '#8697A8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 10, fill: '#44566C' }}
              width={110}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value: number, key: string) => [
                fmtNum(value),
                key === 'new' ? 'New bands' : key === 'reload' ? 'Reloads' : key,
              ]}
              contentStyle={{
                borderRadius: 4,
                border: '1px solid #E9EBF2',
                fontSize: 12,
              }}
            />
            {splitsEnrichmentPending ? (
              <Bar dataKey="orders" fill="#0070AB" radius={[0, 2, 2, 0]} />
            ) : (
              <>
                <Bar dataKey="new" stackId="orders" fill="#0070AB" radius={[0, 0, 0, 0]} />
                <Bar dataKey="reload" stackId="orders" fill="#00C7CA" radius={[0, 2, 2, 0]} />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex items-center justify-center gap-4 text-[10px] text-ink-soft dark:text-slate-400">
        {splitsEnrichmentPending ? (
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-butler-600" /> Orders (new/reload split pending)
          </span>
        ) : (
          <>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-sm bg-butler-600" /> New bands
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-sm bg-midway-500" /> Reloads
            </span>
          </>
        )}
      </div>
    </CardShell>
  )
}

function TopAttractionsCard({ rows }: { rows: TopAttractionRow[] }) {
  if (rows.length === 0) {
    return (
      <CardShell title="Top Attractions" icon={<BarChart3 className="h-4 w-4" />}>
        <EmptyCardBody label="No scans logged yet." />
      </CardShell>
    )
  }
  return (
    <CardShell
      title="Top Attractions"
      icon={<BarChart3 className="h-4 w-4" />}
      hint={`top ${Math.min(rows.length, 8)}`}
    >
      <ul className="flex flex-1 flex-col divide-y divide-hairline text-xs dark:divide-slate-700">
        {rows.slice(0, 8).map((r) => (
          <li key={r.attraction_id} className="flex items-center gap-3 py-1.5">
            <span className="w-5 flex-shrink-0 text-right font-mono text-[10px] text-ink-soft dark:text-slate-400">
              {r.rank}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink dark:text-slate-100">
                {r.attraction_name}
              </span>
              <span className="block text-[10px] uppercase tracking-wide text-ink-soft dark:text-slate-400">
                {r.attraction_type}
              </span>
            </span>
            <span className="flex-shrink-0 text-right">
              <span className="block font-mono text-butler-700 dark:text-butler-200">
                {fmtNum(r.scans)}
              </span>
              <span className="block text-[10px] text-ink-soft dark:text-slate-400">
                {fmtUSD(r.revenue)} · {r.repeat_play_pct}% rpt
              </span>
            </span>
          </li>
        ))}
      </ul>
    </CardShell>
  )
}

function GeographyCard({ payload }: { payload: GeographyPayload }) {
  return (
    <CardShell title="Customer Geography" icon={<MapPin className="h-4 w-4" />}>
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-xs text-ink-soft dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-glow-600 bg-glow-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-glow-800 dark:border-glow-500 dark:bg-glow-900/30 dark:text-glow-200">
          <span className="h-1.5 w-1.5 rounded-full bg-glow-500" aria-hidden="true" />
          {payload.status === 'coming_soon' ? 'Coming soon' : payload.status}
        </span>
        <p className="max-w-[18rem] text-[11px] leading-relaxed">
          {payload.reason ||
            'Customer ZIP rollups land once the identity graph populates.'}
        </p>
      </div>
    </CardShell>
  )
}

export interface DashboardExtrasPaneProps {
  eventId: number
  authToken?: string | null
}

export function DashboardExtrasPane({ eventId, authToken }: DashboardExtrasPaneProps) {
  const [data, setData] = useState<DashboardExtras | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    const headers: Record<string, string> = {}
    if (authToken) headers.Authorization = `Bearer ${authToken}`
    fetch(`/api/dashboard/${eventId}/extras`, {
      headers,
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (res.status === 503) {
          // Dashboard PG path disabled — render empty states gracefully.
          return null
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const body = (await res.json()) as Partial<DashboardExtras>
        // Defensive shape check — if any of the required arrays are absent
        // (stale cache, dev fixture, unexpected response), treat the
        // response as unavailable rather than crashing the card grid.
        const hasShape =
          body &&
          Array.isArray(body.plan_mix) &&
          Array.isArray(body.kiosk_throughput) &&
          Array.isArray(body.top_attractions) &&
          body.geography &&
          typeof body.geography.status === 'string'
        if (!hasShape) {
          return null
        }
        return body as DashboardExtras
      })
      .then((payload) => {
        if (cancelled) return
        setData(payload)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'failed to load extras')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [eventId, authToken])

  if (loading) {
    return (
      <div
        data-testid="dashboard-extras-skeleton"
        className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-72 animate-pulse rounded-card border border-hairline bg-surface-stripe dark:border-slate-700 dark:bg-slate-800"
          />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div
        data-testid="dashboard-extras-error"
        className="mt-4 rounded-card border border-dashed border-marquee-400 bg-marquee-50 p-3 text-xs text-marquee-800 dark:border-marquee-700 dark:bg-marquee-900/30 dark:text-marquee-200"
      >
        Couldn't load drill-down cards: {error}
      </div>
    )
  }

  // `null` happens when the backend PG path is gated off (503). Show all
  // four cards in their empty states so the layout shape doesn't collapse.
  const payload: DashboardExtras = data ?? {
    source: 'pg',
    event_id: eventId,
    plan_mix: [],
    kiosk_throughput: [],
    top_attractions: [],
    geography: { status: 'coming_soon', reason: 'Dashboard PG path is offline.' },
    generated_at: new Date().toISOString(),
  }

  return (
    <div
      data-testid="dashboard-extras"
      className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
    >
      <PlanMixCard rows={payload.plan_mix} />
      <KioskThroughputCard rows={payload.kiosk_throughput} />
      <TopAttractionsCard rows={payload.top_attractions} />
      <GeographyCard payload={payload.geography} />
    </div>
  )
}
